import { currentLang, t } from './i18n';
import {
  cachedAllGoals, setCachedAllGoals,
  setCachedGoalsJSON,
  currentDeadlineFilter,
  collapsedAiResults,
  expandedSubtaskDescriptions,
  openRejectFeedbackIds,
  pendingSubtaskGoalIds,
  updateCachedGoal,
  updateCachedSubtask,
  removeCachedGoal,
  removeCachedSubtask,
} from './state';
import { api } from './api';
import { showToast, showConfirmModal, showToastWithAction } from './toast';
import { getLocalToday, formatDeadlineLabel, formatDeadlineBucketLabel, isOverdue, itemMatchesDeadlineFilter, parseDeadlineDate } from './helpers';
import { playPenSwoosh } from './effects';
import { loadAllGoals, renderPendingArea, startSubtaskPolling } from './upload';
import { escapeHtml } from './html-escape';
import { renderGoalViews } from './goals';
import { renderSearch, setDeadlineFilter } from './filters';
import { getErrorMessage } from './types';
import type { ActorQuestionAnswer, Goal, GoalWithSubtasks, DatePickerOptions } from './types';
import { sanitizeImageUrl } from './url-sanitizer';
import {
  canonicalJudgmentEntries,
  createCanonicalJudgmentInteractionIdempotencyKey,
  getCanonicalJudgmentSelectionSurface,
} from './canonical-judgment';
import type { GoalDodRowsSaveDetail } from './components/goal-dod-rows-editor';
import type { GoalDodRow } from './goal-dod-rows';

// #2489: PATCH /api/goals/:id answers a refused edit with a 409 code; the
// wording comes from here, never from the server's message.
const GOAL_UPDATE_CONFLICT_KEYS: Record<string, string> = {
  goal_has_judgment: 'goalUpdateConflictJudgment',
  goal_update_pending: 'goalUpdateConflictPending',
  goal_done: 'goalUpdateConflictDone',
  goal_not_active: 'goalUpdateConflictNotActive',
  goal_update_partially_applied: 'goalUpdateConflictPartial',
  goal_update_conflict: 'goalUpdateConflictGeneric',
  goal_judgment_row_edit: 'goalUpdateConflictJudgmentRow',
  goal_judgment_hold_required: 'goalUpdateConflictJudgmentHold',
  goal_dod_edit_requires_conditions: 'goalUpdateConflictDodRows',
  goal_compat_write_refused: 'goalUpdateConflictCompatWrite',
  goal_dod_last_row: 'goalUpdateConflictDodLastRow',
  goal_condition_proposal_stale: 'goalUpdateConflictProposalStale',
};

function goalUpdateErrorMessage(err: unknown, fallbackKey: string): string {
  const code = (err as { code?: unknown } | null)?.code;
  if (typeof code === 'string' && Object.prototype.hasOwnProperty.call(GOAL_UPDATE_CONFLICT_KEYS, code)) {
    return t(GOAL_UPDATE_CONFLICT_KEYS[code]) as string;
  }
  return t(fallbackKey, { msg: getErrorMessage(err) }) as string;
}

export const AI_ACTION_ICONS_WEB = {
  draft_message:   'edit_note',
  check_calendar:  'event',
  schedule_meeting:'group',
  summarize_info:  'summarize',
  lookup_info:     'search',
  compare_options: 'compare_arrows',
  send_reminder:   'notifications',
  track_progress:  'monitoring',
  send_message:    'send',
  create_event:    'event_available',
  delete_event:    'event_busy',
  ai_work:         'smart_toy',
  // #2732 stage 7b: routed verbs without a legacy label of their own (ai-work-kind.ts).
  respond_invite:  'how_to_reg',
  make:            'description',
  register:        'app_registration',
  apply:           'assignment',
  update:          'edit',
  organize:        'folder',
  delete:          'delete',
  approve:         'approval',
  site_procedure:  'language',
  pay:             'payments',
};
export const AI_ACTION_LABELS_WEB = {
  draft_message:   { ja: 'メール下書き', en: 'Draft message' },
  check_calendar:  { ja: 'カレンダー確認', en: 'Check calendar' },
  schedule_meeting:{ ja: '会議設定', en: 'Schedule meeting' },
  summarize_info:  { ja: '情報まとめ', en: 'Summarize' },
  lookup_info:     { ja: '情報検索', en: 'Search info' },
  compare_options: { ja: '選択肢比較', en: 'Compare options' },
  send_reminder:   { ja: 'リマインダー', en: 'Reminder' },
  track_progress:  { ja: '進捗追跡', en: 'Track progress' },
  send_message:    { ja: 'メッセージ送信', en: 'Send message' },
  create_event:    { ja: '予定の作成', en: 'Create event' },
  delete_event:    { ja: '予定の削除', en: 'Delete event' },
  ai_work:         { ja: 'AIの作業', en: 'AI work' },
  respond_invite:  { ja: '出欠の返事', en: 'Reply to invitation' },
  make:            { ja: '作成', en: 'Make' },
  register:        { ja: '登録', en: 'Register' },
  apply:           { ja: '申請', en: 'Apply' },
  update:          { ja: '修正', en: 'Update' },
  organize:        { ja: '整理', en: 'Organize' },
  delete:          { ja: '削除', en: 'Delete' },
  approve:         { ja: '承認', en: 'Approve' },
  site_procedure:  { ja: 'サイトで手続き', en: 'Website procedure' },
  pay:             { ja: '支払い', en: 'Pay' },
};

const togglingSubtasks = new Set();
const togglingGoals = new Set<string>();
const completingGoals = new Set<string>();
// The option the user just chose, per Goal, keyed by the packet it was chosen
// from. The card shows it as chosen until the reload after the save lands.
const canonicalSelectionPending = new Map<string, { selectionSnapshotToken: string; optionKey: string }>();
const reviewProposalPending = new Set<string>();
export const GOAL_COMPLETION_EXIT_MS = 460;

export function isGoalToggling(goalId: string): boolean {
  return togglingGoals.has(goalId);
}

export function isGoalCompleting(goalId: string): boolean {
  return completingGoals.has(goalId);
}

export function isCanonicalJudgmentSelectionPending(goalId: string): boolean {
  return canonicalSelectionPending.has(goalId);
}

export function canonicalJudgmentChosenOptionKey(goalId: string, selectionSnapshotToken: string | undefined): string | null {
  const chosen = canonicalSelectionPending.get(goalId);
  return chosen && chosen.selectionSnapshotToken === selectionSnapshotToken ? chosen.optionKey : null;
}

export function isReviewProposalPending(goalId: string): boolean {
  return reviewProposalPending.has(goalId);
}

function renderWebGoalState() {
  renderGoalViews();
  renderSearch();
  renderPendingArea();
  // The open goal detail is given the same goal object until the goals are
  // read again, so it would not draw a choice, an answer or a rebuild kept
  // here until then. Ask it to draw now.
  document.querySelectorAll<HTMLElement & { requestUpdate?: () => void }>('zen-goal-detail').forEach((el) => el.requestUpdate?.());
}

function waitForNextFrame(): Promise<void> {
  return new Promise(resolve => requestAnimationFrame(() => resolve()));
}

/** `quiet`: the caller shows its own message (the list's achievement card, #2749). */
export async function toggleGoalDone(goalId: string, isDone: boolean, options: { quiet?: boolean } = {}): Promise<boolean> {
  if (togglingGoals.has(goalId)) return false;
  const goal = cachedAllGoals.find(item => item.id === goalId);
  if (!goal) return false;
  const nextStatus = isDone ? 'active' : 'done';
  const previousGoal = {
    ...goal,
    subtasks: (goal.subtasks || []).map(st => ({ ...st })),
    decisions: (goal.decisions || []).map(st => ({ ...st })),
  };
  let completionExitTimer: number | undefined;

  try {
    togglingGoals.add(goalId);
    if (!isDone) {
      completingGoals.add(goalId);
    }
    updateCachedGoal(goalId, currentGoal => ({
      ...currentGoal,
      status: nextStatus,
      done_at: !isDone ? new Date().toISOString() : null,
    }));
    renderWebGoalState();
    if (!isDone) {
      completionExitTimer = window.setTimeout(() => {
        completingGoals.delete(goalId);
        renderWebGoalState();
      }, GOAL_COMPLETION_EXIT_MS);
    }
    await waitForNextFrame();

    if (!isDone) {
      playPenSwoosh();
    }
    if (!options.quiet) showToast(!isDone ? t('goalCompleted') as string : t('goalUncompleted') as string, 'success');

    const response = await api(`/api/goals/${goalId}`, {
      method: 'PATCH',
      json: { status: nextStatus }
    });
    if (response?.goal) {
      updateCachedGoal(goalId, () => response.goal);
    }
    if (isDone) {
      renderWebGoalState();
    }
    return true;
  } catch (err) {
    if (completionExitTimer !== undefined) window.clearTimeout(completionExitTimer);
    completingGoals.delete(goalId);
    updateCachedGoal(goalId, () => previousGoal);
    renderWebGoalState();
    showToast(goalUpdateErrorMessage(err, 'updateError'));
    return false;
  } finally {
    togglingGoals.delete(goalId);
    if (isDone) {
      completingGoals.delete(goalId);
      renderWebGoalState();
    }
  }
}

/** Resolves true once the change is saved (#3549: the Web list takes its row back otherwise). */
export async function toggleSubtask(subtaskId: string, isDone: boolean): Promise<boolean> {
  if (togglingSubtasks.has(subtaskId)) return false;
  togglingSubtasks.add(subtaskId);
  const goal = cachedAllGoals.find(item =>
    [...(item.decisions || []), ...(item.subtasks || [])].some(subtask => subtask.id === subtaskId)
  );
  const currentSubtask = goal
    ? [...(goal.decisions || []), ...(goal.subtasks || [])].find(subtask => subtask.id === subtaskId) || null
    : null;
  if (!goal || !currentSubtask) {
    togglingSubtasks.delete(subtaskId);
    return false;
  }
  const nextStatus = isDone ? 'pending' : 'done';
  const nextDoneAt = isDone ? null : new Date().toISOString();
  const previousStatus = currentSubtask.status;
  const previousDoneAt = currentSubtask.done_at;
  const previousDodItems = (currentSubtask.dod_items || []).map(item => ({ ...item }));
  // DoD チェックリストは完了状態に追従する（サーバ側 updateSubtask と同じ挙動を楽観的に反映）
  const nextDodItems = previousDodItems.map(item => ({ ...item, done: !isDone }));

  if (!isDone) {
    playPenSwoosh();
  }
  updateCachedSubtask(subtaskId, subtask => ({ ...subtask, status: nextStatus, done_at: nextDoneAt, dod_items: nextDodItems }));
  renderWebGoalState();
  if (!isDone) {
    showToast(t('subtaskCompleted') as string, 'success');
  }

  try {
    const resp = await api(`/api/subtasks/${subtaskId}`, {
      method: 'PATCH',
      json: { status: nextStatus }
    });
    // The server moves a review's note on the task (`review_closed`, #2798).
    if (resp.subtask?.metadata !== undefined) {
      updateCachedSubtask(subtaskId, subtask => ({ ...subtask, metadata: resp.subtask.metadata }));
      renderWebGoalState();
    }

    // #1929 (S4): the server suggests completion only from the review's
    // candidate; completing accepts that candidate, not a direct status change.
    const completionId = resp.goal_completion_suggested ? resp.completion_candidate?.proposal_candidate_id : null;
    if (!isDone && typeof completionId === 'string') {
      const msg = t('completionSuggestedMsg', { title: goal.title }) as string;
      showToastWithAction(msg, t('completionSuggestedAction') as string, async () => {
        try {
          await api(`/api/goals/${encodeURIComponent(goal.id)}/proposals/${encodeURIComponent(completionId)}/action`, {
            method: 'POST',
            json: { action: 'accept' },
          });
          showToast(t('completionSuggestedDone') as string, 'success');
        } catch (err) {
          showToast(t('updateError', { msg: getErrorMessage(err) }) as string);
        }
        await loadAllGoals({ silent: true }).catch(() => {});
      });
    }
    return true;
  } catch (err) {
    updateCachedSubtask(subtaskId, subtask => ({ ...subtask, status: previousStatus, done_at: previousDoneAt, dod_items: previousDodItems }));
    renderWebGoalState();
    showToast(t('updateError', { msg: getErrorMessage(err) }) as string);
    return false;
  } finally {
    togglingSubtasks.delete(subtaskId);
  }
}

/** #2798: principal keeps open a task the review proposed closing. */
export async function declineReviewCloseProposal(subtaskId: string): Promise<boolean> {
  try {
    const resp = await api(`/api/subtasks/${subtaskId}/review-close-proposal/decline`, { method: 'POST' });
    if (resp.subtask) updateCachedSubtask(subtaskId, subtask => ({ ...subtask, metadata: resp.subtask.metadata }));
    renderWebGoalState();
    showToast(currentLang === 'en' ? 'Kept the task open.' : 'タスクを残しました', 'success');
    return true;
  } catch (err) {
    showToast(t('updateError', { msg: getErrorMessage(err) }) as string);
    return false;
  }
}

export function updateSubtaskCounter(card: HTMLElement, delta: number) {
  const labelEl = card.querySelector('.subtasks-label');
  if (!labelEl) return;
  const match = labelEl.textContent.match(/\((\d+)\/(\d+)\)/);
  if (match) {
    const done = parseInt(match[1]) + delta;
    const total = parseInt(match[2]);
    labelEl.textContent = `${t('tasksLabel')} (${done}/${total})`;
  }
}

export async function toggleDod(subtaskId: string, index: number, newDone: boolean) {
  let previousDodItems: Array<{ text: string; done: boolean }> | null = null;
  let nextDodItems: Array<{ text: string; done: boolean }> | null = null;
  const updated = updateCachedSubtask(subtaskId, subtask => {
    previousDodItems = (subtask.dod_items || []).map(item => ({ ...item }));
    nextDodItems = (subtask.dod_items || []).map((item, itemIndex) => itemIndex === index
      ? { ...item, done: newDone }
      : { ...item });
    return { ...subtask, dod_items: nextDodItems || subtask.dod_items };
  });
  if (!updated || !nextDodItems) return;
  renderWebGoalState();

  try {
    await api(`/api/subtasks/${subtaskId}`, {
      method: 'PATCH',
      json: { dod_items: nextDodItems },
    });
  } catch (err) {
    if (previousDodItems) {
      updateCachedSubtask(subtaskId, subtask => ({ ...subtask, dod_items: previousDodItems || subtask.dod_items }));
      renderWebGoalState();
    }
    showToast(t('dodError', { msg: getErrorMessage(err) }) as string);
  }
}

/** `onConfirm`: runs once the deletion is confirmed, before it is saved (#3549). Resolves true once deleted. */
export async function deleteSubtaskUI(subtaskId: string, title: string, onConfirm?: () => void): Promise<boolean> {
  if (!confirm(t('confirmDelete', { title: title || '?' }) as string)) return false;
  onConfirm?.();
  try {
    await api(`/api/subtasks/${subtaskId}`, { method: 'DELETE' });
    removeCachedSubtask(subtaskId);
    expandedSubtaskDescriptions.delete(subtaskId);
    openRejectFeedbackIds.delete(subtaskId);
    collapsedAiResults.delete(subtaskId);
    renderWebGoalState();
    showToast(t('goalDeleted') as string, 'info');
    return true;
  } catch (err) {
    showToast(t('goalDeleteFailed') + ': ' + getErrorMessage(err));
    return false;
  }
}

export async function resetDecisionUI(subtaskId: string) {
  try {
    await api(`/api/subtasks/${subtaskId}/decide`, { method: 'DELETE' });
    updateCachedSubtask(subtaskId, subtask => ({ ...subtask, decision_result: null }));
    renderWebGoalState();
    showToast(t('decisionResetDone') as string, 'info');
  } catch (err) {
    showToast(t('decisionResetError', { msg: getErrorMessage(err) }) as string);
  }
}

export function isFeedback(text: string) {
  if (text.length > 20) return true;
  const feedbackKeywords = [
    '不要', '削除', '変更', '修正', 'いらない', 'やめ', '代わりに',
    '追加して', '変えて', '直して', '完了', '済み', '終わ', 'もう',
    'remove', 'delete', 'change', 'instead', "don't need",
    'already', 'done', 'finished'
  ];
  const lower = text.toLowerCase();
  if (feedbackKeywords.some(kw => lower.includes(kw))) return true;
  if (/(?:しました|ました|ません|ください|してほしい|して$|です$)/.test(text)) return true;
  return false;
}

/** `field`: the box it was typed in, when it is not the goal card's own. */
export async function addSubtask(goalId: string, field?: HTMLInputElement | null) {
  const input = field ?? document.getElementById('addst_' + goalId) as HTMLInputElement;
  const title = input.value.trim();
  if (!title) return;

  if (isFeedback(title)) {
    input.disabled = true;
    try {
      await api(`/api/goals/${goalId}/feedback`, {
        method: 'POST',
        json: { text: title }
      });
      input.value = '';
      showToast(t('feedbackRecorded') as string, 'success');
    } catch (err) {
      showToast(t('errorGeneral') + ': ' + getErrorMessage(err), 'error');
    } finally {
      input.disabled = false;
      input.focus();
    }
  } else {
    input.disabled = true;
    try {
      await api(`/api/goals/${goalId}/subtasks`, {
        method: 'POST',
        json: { title }
      });
      input.value = '';
      loadAllGoals();
    } catch (err) {
      showToast(t('errorGeneral') + ': ' + getErrorMessage(err), 'error');
    } finally {
      input.disabled = false;
      input.focus();
    }
  }
}

export async function askAI(goalId: string, goalTitle: string) {
  const text = currentLang === 'en'
    ? `"${goalTitle || goalId}" from Zenmetry\nPlease use get_goal(goal_id="${goalId}") to check the details first.`
    : `「${goalTitle || goalId}」について相談したい。\nZenmetryゴール / get_goal(goal_id="${goalId}") で詳細を確認してから相談に乗ってほしい。`;

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  let copied = false;
  try {
    await navigator.clipboard.writeText(text);
    copied = true;
  } catch (e) {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;opacity:0;';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    copied = true;
  }

  if (isMobile) {
    showToast(t('copyClaudeMobile') as string, 'info', 5000);
  } else {
    showToast(t('copyClaudeDesktop') as string);
    window.open('https://claude.ai/new', '_blank');
  }

  try {
    await api(`/api/goals/${goalId}/subtasks`, {
      method: 'POST',
      json: { title: t('askAISubtask') as string, executor: 'human' }
    });
    loadAllGoals();
  } catch (e) {}
}

export async function makeDecision(subtaskId: string, result: string, label?: string) {
  const displayLabel = label || result;
  if (!confirm(t('decisionConfirm', { label: displayLabel }) as string)) return;

  try {
    const data = await api(`/api/subtasks/${subtaskId}/decide`, {
      method: 'POST',
      json: { result }
    });
    if (data.goal_deleted) {
      showToast(t('goalDeleted') as string, 'success');
      loadAllGoals();
      return;
    }
    showToast(t('decisionSuccess') as string, 'success');
    await maybeCreateFollowupGoal(subtaskId, result, data.followup_goal_suggestion);
    await loadAllGoals();
  } catch (err) {
    showToast(t('decisionError', { msg: getErrorMessage(err) }) as string);
  }
}

type CanonicalSelectionError = Error & {
  code?: string;
  judgment_packet?: Goal['simplified_action_intake'] extends infer T
    ? T extends { judgment_packet?: infer P } ? P : never
    : never;
};

function applyCanonicalJudgmentPacket(goalId: string, packet: NonNullable<NonNullable<Goal['simplified_action_intake']>['judgment_packet']>): boolean {
  if (!getCanonicalJudgmentSelectionSurface(packet)) return false;
  return updateCachedGoal(goalId, (currentGoal) => {
    if (!currentGoal.simplified_action_intake) return currentGoal;
    return {
      ...currentGoal,
      simplified_action_intake: {
        ...currentGoal.simplified_action_intake,
        judgment_packet: packet,
      },
    };
  });
}

// #2598: once the choice is saved, the Goal is revised and reviewed, which
// rebuilds its Actions some seconds later. Until the rebuilt work arrives the
// detail says the work is being rebuilt, so the work from before the choice is
// not read as the plan. The server reads `review_in_progress` once the review
// job exists; the revision before it can take some seconds, so the detail says
// so from the click. The Goals are read again every few seconds meanwhile.
// The rebuild after 「誰が進めますか」 runs without a review job, so the detail
// also stops saying so once the Goal's work has changed.
const rebuildingGoals = new Map<string, { startedAt: number; seenInProgress: boolean; work: string }>();
const REBUILD_READ_INTERVAL_MS = 3000;
// The review job has not appeared by then: the revision ended without one.
const REBUILD_UNSEEN_GIVE_UP_MS = 45000;
const REBUILD_GIVE_UP_MS = 180000;
let rebuildReadTimer: number | null = null;

export function isGoalRebuilding(goal: Pick<Goal, 'id' | 'simplified_action_intake'>): boolean {
  return Boolean(goal.simplified_action_intake?.review_in_progress) || rebuildingGoals.has(goal.id);
}

function goalWorkSignature(goal: Pick<GoalWithSubtasks, 'subtasks' | 'decisions'> | undefined): string {
  return [...(goal?.subtasks ?? []), ...(goal?.decisions ?? [])].map(subtask => subtask.id).sort().join(',');
}

function settleRebuildingGoals() {
  const now = Date.now();
  for (const [goalId, watch] of rebuildingGoals) {
    const goal = cachedAllGoals.find(item => item.id === goalId);
    if (goal?.simplified_action_intake?.review_in_progress) watch.seenInProgress = true;
    else if (watch.seenInProgress || !goal || goalWorkSignature(goal) !== watch.work) rebuildingGoals.delete(goalId);
    const elapsed = now - watch.startedAt;
    if (elapsed > REBUILD_GIVE_UP_MS || (!watch.seenInProgress && elapsed > REBUILD_UNSEEN_GIVE_UP_MS)) rebuildingGoals.delete(goalId);
  }
}

export function watchGoalRebuild(goalId: string) {
  const goal = cachedAllGoals.find(item => item.id === goalId);
  rebuildingGoals.set(goalId, { startedAt: Date.now(), seenInProgress: false, work: goalWorkSignature(goal) });
  renderWebGoalState();
  if (rebuildReadTimer !== null) return;
  const read = () => {
    rebuildReadTimer = null;
    loadAllGoals({ silent: true }).catch(() => {}).finally(() => {
      settleRebuildingGoals();
      renderWebGoalState();
      if (rebuildingGoals.size > 0) rebuildReadTimer = window.setTimeout(read, REBUILD_READ_INTERVAL_MS);
    });
  };
  rebuildReadTimer = window.setTimeout(read, REBUILD_READ_INTERVAL_MS);
}

/**
 * Submit a selection from the canonical packet only. This is a user judgment
 * write; it never invokes an execution path. A stale response is rendered as
 * the returned packet and requires a new click, with no automatic retry.
 */
export async function selectCanonicalJudgmentOption(
  goalId: string,
  optionKey: string,
  optionValue: string,
  selectionSnapshotToken: string,
  coordinate?: { condition_ref: string; axis: string },
  /** #2951: the place typed in a place card's box, sent with the box's option. */
  text?: string,
): Promise<boolean> {
  if (canonicalSelectionPending.has(goalId)) return false;
  const goal = cachedAllGoals.find(item => item.id === goalId);
  // #2422 stage 2: a Goal with several judgments keeps one packet per judgment;
  // the token names the packet the user chose from.
  const entries = canonicalJudgmentEntries(goal?.simplified_action_intake);
  const severalJudgments = entries.length > 1;
  const packet = entries.find(entry => entry.packet.selection_snapshot_token === selectionSnapshotToken)?.packet;
  const box = packet?.text_input;
  const typed = text?.trim() ?? '';
  const option = box && typed && box.option_key === optionKey && box.option_value === optionValue
    ? { key: box.option_key, value: box.option_value }
    : getCanonicalJudgmentSelectionSurface(packet)?.options.find(candidate => candidate.key === optionKey && candidate.value === optionValue);
  // The list draws its rows before the goals are read, a page at a time on a
  // large account. A choice from a row whose goal is not read yet goes with
  // the row's own option and token; the server checks them against the packet
  // it holds.
  if (!goal) {
    if (!optionKey || !optionValue || !selectionSnapshotToken) return false;
  } else if (!packet || !option || packet.selection_snapshot_token !== selectionSnapshotToken) {
    return false;
  }
  const chosen = option ?? { key: optionKey, value: optionValue };

  // Show the choice on the card right away; neither the save nor the reload
  // after it is waited on.
  canonicalSelectionPending.set(goalId, { selectionSnapshotToken, optionKey: chosen.key });
  renderWebGoalState();
  try {
    await api(`/api/goals/${encodeURIComponent(goalId)}/judgment-selection`, {
      method: 'POST',
      json: {
        option_key: chosen.key,
        option_value: chosen.value,
        selection_snapshot_token: selectionSnapshotToken,
        interaction_idempotency_key: createCanonicalJudgmentInteractionIdempotencyKey(),
        ...(coordinate ? { condition_ref: coordinate.condition_ref, axis: coordinate.axis } : {}),
        ...(typed && (box ? chosen.key === box.option_key : !goal) ? { text: typed } : {}),
        // #2598: the Goal is revised after the answer; the reads below show it.
        revise_after_response: true,
      },
    });
    showToast(currentLang === 'en' ? 'Your decision was saved.' : '判断を保存しました。', 'success');
    // Read the saved state in the background; the card keeps showing the
    // choice until it arrives.
    loadAllGoals({ silent: true }).catch(() => {}).finally(() => {
      canonicalSelectionPending.delete(goalId);
      renderWebGoalState();
    });
    watchGoalRebuild(goalId);
    return true;
  } catch (error) {
    // Go back to the state the server holds.
    canonicalSelectionPending.delete(goalId);
    renderWebGoalState();
    const selectionError = error as CanonicalSelectionError;
    if (selectionError.code === 'stale_input' && (selectionError.judgment_packet || severalJudgments)) {
      // With several judgments the returned packet replaces one of them; read
      // the Goal again instead of guessing which.
      if (!severalJudgments && applyCanonicalJudgmentPacket(goalId, selectionError.judgment_packet)) {
        renderWebGoalState();
      } else {
        setCachedGoalsJSON(null);
        await loadAllGoals({ silent: true });
      }
      showToast(currentLang === 'en'
        ? 'The options changed. Please choose again.'
        : '選択肢が更新されました。もう一度選択してください。', 'info');
    } else if (selectionError.code === 'capability_not_ready') {
      showToast(currentLang === 'en' ? 'This choice is not available yet.' : 'この選択はまだ利用できません。', 'info');
    } else if (selectionError.code === 'option_mismatch' || selectionError.code === 'invalid_state' || selectionError.code === 'settlement_malformed' || selectionError.code === 'settlement_pointer_missing' || selectionError.code === 'settlement_conflict') {
      showToast(currentLang === 'en' ? 'This choice is no longer available.' : 'この選択は現在利用できません。', 'info');
    } else {
      showToast(currentLang === 'en' ? 'Could not save your decision.' : '判断を保存できませんでした。', 'error');
    }
  }
  renderWebGoalState();
  return false;
}

// #2732: the 「誰が進めますか」 answer just given, per Goal and condition
// ('' while a hold is saved). The card shows it until the reload lands.
const actorAnswerPending = new Map<string, string>();
const actorAnswerKey = (goalId: string, conditionRef: string) => `${goalId}:${conditionRef}`;

/** The option key an actor answer shows as: the actor, or `teammate:<user id>`. */
export function actorAnswerOptionKey(answer: ActorQuestionAnswer | null | undefined): string | null {
  if (!answer) return null;
  return answer.actor === 'teammate' ? `teammate:${answer.teammate_user_id}` : answer.actor;
}

export function pendingActorAnswerOptionKey(goalId: string, conditionRef: string): string | null {
  return actorAnswerPending.get(actorAnswerKey(goalId, conditionRef)) ?? null;
}

/**
 * #2732: answer 「誰が進めますか」, answer it again, or put the answer on hold
 * (`actor` "hold"). The card shows the answer at once; the Goal's work is
 * rebuilt on the server and the detail says so until it lands.
 */
export async function answerGoalActor(goalId: string, conditionRef: string, actor: 'principal' | 'teammate' | 'external_party' | 'hold', teammateUserId?: string): Promise<boolean> {
  const key = actorAnswerKey(goalId, conditionRef);
  if (actorAnswerPending.has(key)) return false;
  actorAnswerPending.set(key, actor === 'hold' ? '' : actor === 'teammate' ? `teammate:${teammateUserId}` : actor);
  renderWebGoalState();
  const en = currentLang === 'en';
  let saved = false;
  try {
    await api(`/api/goals/${encodeURIComponent(goalId)}/actor-answer`, {
      method: 'POST',
      json: { condition_ref: conditionRef, actor, ...(teammateUserId ? { teammate_user_id: teammateUserId } : {}) },
    });
    showToast(actor === 'hold'
      ? (en ? 'Put on hold.' : '保留にしました。')
      : (en ? 'Saved.' : '担当を記録しました。'), 'success');
    watchGoalRebuild(goalId);
    saved = true;
  } catch {
    showToast(en ? 'Could not save.' : '記録できませんでした。', 'error');
  }
  // The card keeps the answer until the goals are read again behind it.
  loadAllGoals({ silent: true }).catch(() => {}).finally(() => {
    actorAnswerPending.delete(key);
    renderWebGoalState();
  });
  return saved;
}

/**
 * #2537: put the current choice of a judgment on hold. The token names the
 * choice the user saw; the judgment shows as open at once and the Goal is read
 * again once the hold is saved. A stale response reads the Goal again.
 */
export async function holdCanonicalJudgmentChoice(
  goalId: string,
  selectionSnapshotToken: string,
  coordinate?: { condition_ref: string; axis: string },
) {
  if (canonicalSelectionPending.has(goalId)) return;
  // An empty option key shows no option as chosen while the hold is saved.
  canonicalSelectionPending.set(goalId, { selectionSnapshotToken, optionKey: '' });
  renderWebGoalState();
  try {
    await api(`/api/goals/${encodeURIComponent(goalId)}/judgment-hold`, {
      method: 'POST',
      json: {
        selection_snapshot_token: selectionSnapshotToken,
        ...(coordinate ? { condition_ref: coordinate.condition_ref, axis: coordinate.axis } : {}),
      },
    });
    showToast(currentLang === 'en' ? 'Put the decision on hold.' : '判断を保留にしました。', 'success');
    loadAllGoals({ silent: true }).catch(() => {}).finally(() => {
      canonicalSelectionPending.delete(goalId);
      renderWebGoalState();
    });
    // The review rebuilds the work of the held path.
    watchGoalRebuild(goalId);
    return;
  } catch (error) {
    canonicalSelectionPending.delete(goalId);
    const holdError = error as CanonicalSelectionError;
    if (holdError.code === 'stale_input' || holdError.code === 'not_chosen') {
      setCachedGoalsJSON(null);
      await loadAllGoals({ silent: true });
      showToast(currentLang === 'en'
        ? 'The decision changed. Please check it again.'
        : '判断が更新されました。もう一度確かめてください。', 'info');
    } else {
      showToast(currentLang === 'en' ? 'Could not put the decision on hold.' : '判断を保留にできませんでした。', 'error');
    }
  }
  renderWebGoalState();
}

// #2086: the principal answers a change the review proposed. "keep" is
// remembered until a newer message arrives; "withdraw" deletes the named
// condition with the lens version the principal saw. Nothing else changes.
const REVIEW_PROPOSAL_TOAST = {
  keep: 'reviewProposalKept', withdraw: 'reviewProposalWithdrawn', add: 'reviewProposalAdded', rewrite: 'reviewProposalRewritten',
} as const;

// #2086: the principal's answer to a review proposal. Removing, adding and
// rewriting a condition are condition edits (the drafted sentence is sent as
// the principal's own words); keeping is recorded on the proposal.
export async function answerReviewProposal(
  goalId: string,
  proposalRef: string,
  answer: 'keep' | 'withdraw' | 'add' | 'rewrite',
  conditionRef?: string,
  draft?: string,
) {
  if (reviewProposalPending.has(goalId)) return;
  const goal = cachedAllGoals.find(item => item.id === goalId);
  if (!goal) return;
  if (answer !== 'keep' && typeof goal.lens_version !== 'number') return;
  if ((answer === 'withdraw' || answer === 'rewrite') && !conditionRef) return;
  if ((answer === 'add' || answer === 'rewrite') && !draft?.trim()) return;
  const edit = answer === 'withdraw' ? { op: 'delete', condition_ref: conditionRef }
    : answer === 'add' ? { op: 'add', text: draft!.trim() }
    : answer === 'rewrite' ? { op: 'rewrite', condition_ref: conditionRef, text: draft!.trim() }
    : null;
  reviewProposalPending.add(goalId);
  renderWebGoalState();
  try {
    await api(`/api/goals/${encodeURIComponent(goalId)}`, {
      method: 'PATCH',
      json: edit
        ? { dod_edits: { expected_lens_version: goal.lens_version, edits: [edit] }, edit_entrypoint: 'web_review_proposal' }
        : { review_proposal_answers: { answers: [{ proposal_ref: proposalRef, answer: 'keep' }] }, edit_entrypoint: 'web_review_proposal' },
    });
    if (answer === 'add' || answer === 'rewrite') {
      // The added or rewritten condition gets its 見立て from the review that follows.
      pendingSubtaskGoalIds.add(goalId);
      startSubtaskPolling();
    }
    showToast(t(REVIEW_PROPOSAL_TOAST[answer]) as string, 'info');
  } catch (err) {
    showToast(goalUpdateErrorMessage(err, 'updateError'));
  } finally {
    reviewProposalPending.delete(goalId);
    await loadAllGoals({ silent: true }).catch(() => {});
    renderWebGoalState();
  }
}

async function maybeCreateFollowupGoal(subtaskId: string, result: string, suggestion: any) {
  if (suggestion?.type !== 'create_followup_goal' || !suggestion.title) return;
  if (!confirm(t('followupGoalConfirm', { title: suggestion.title }) as string)) return;
  try {
    await api(`/api/subtasks/${subtaskId}/followup-goal`, {
      method: 'POST',
      json: { decision_result: result },
    });
    showToast(t('followupGoalCreated') as string, 'success');
  } catch (followupErr) {
    showToast(t('followupGoalCreateError', { msg: getErrorMessage(followupErr) }) as string);
  }
}

export async function submitFreeDecision(subtaskId: string) {
  const input = document.getElementById('dfree_' + subtaskId) as HTMLInputElement | null;
  const result = input?.value?.trim();
  if (!result) {
    showToast(t('freeDecisionRequired') as string);
    return;
  }

  try {
    const data = await api(`/api/subtasks/${subtaskId}/decide`, {
      method: 'POST',
      json: { result }
    });
    if (data.goal_deleted) {
      showToast(t('goalDeleted') as string, 'success');
      loadAllGoals();
      return;
    }
    showToast(t('decisionSuccess') as string, 'success');
    await maybeCreateFollowupGoal(subtaskId, result, data.followup_goal_suggestion);
    await loadAllGoals();
  } catch (err) {
    showToast(t('decisionError', { msg: getErrorMessage(err) }) as string);
  }
}

export function getLocalTodayISO() {
  const t = getLocalToday();
  return `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`;
}

export function showDatePickerPopover(anchorEl: HTMLElement, opts: DatePickerOptions) {
  const existing = document.getElementById('dp-popover');
  if (existing) existing.remove();

  const todayISO = getLocalTodayISO();
  const initDate = opts.currentISO ? new Date(opts.currentISO + 'T00:00:00') : new Date();
  let viewYear = initDate.getFullYear();
  let viewMonth = initDate.getMonth();
  let currentTimeValue = opts.currentTime || '';
  let selectedISO = opts.currentISO || null;

  function cleanup() {
    document.removeEventListener('mousedown', outsideClick);
    document.removeEventListener('keydown', escapeKey);
  }

  function outsideClick(e) {
    const pop = document.getElementById('dp-popover');
    if (pop && !pop.contains(e.target) && !anchorEl.contains(e.target)) {
      pop.remove();
      cleanup();
    }
  }

  function escapeKey(e) {
    if (e.key === 'Escape') {
      const pop = document.getElementById('dp-popover');
      if (pop) { pop.remove(); cleanup(); }
    }
  }

  function renderPopover() {
    const old = document.getElementById('dp-popover');
    if (old) old.remove();

    const popover = document.createElement('div');
    popover.id = 'dp-popover';
    popover.className = 'date-picker-popover';

    function handleDayClick(iso) {
      if (opts.enableTime) {
        const h = (document.getElementById('dp-popover')?.querySelector('.dp-time-hour') as HTMLSelectElement | null)?.value || '';
        const m = (document.getElementById('dp-popover')?.querySelector('.dp-time-min') as HTMLSelectElement | null)?.value || '';
        currentTimeValue = (h && m) ? `${h}:${m}` : '';
        selectedISO = iso;
        renderPopover();
      } else {
        opts.onSave(iso);
        popover.remove();
        cleanup();
      }
    }

    const monthNames = t('monthNames') as string[];
    const monthLabel = currentLang === 'en'
      ? `${monthNames[viewMonth]} ${viewYear}`
      : `${viewYear}年 ${monthNames[viewMonth]}`;

    const header = document.createElement('div');
    header.className = 'dp-header';
    header.innerHTML = `
      <button class="dp-nav" id="dp-prev">&#8249;</button>
      <span class="dp-month-label">${monthLabel}</span>
      <button class="dp-nav" id="dp-next">&#8250;</button>
    `;
    popover.appendChild(header);

    const grid = document.createElement('div');
    grid.className = 'dp-grid';

    (t('weekdays') as string[]).forEach(wd => {
      const cell = document.createElement('span');
      cell.className = 'dp-weekday';
      cell.textContent = wd;
      grid.appendChild(cell);
    });

    const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

    const rf = opts.rangeFrom || null;
    const rt = opts.rangeTo || null;
    function applyRangeClasses(btn, iso) {
      if (!rf || !rt || rf >= rt) return;
      if (iso > rf && iso < rt) btn.classList.add('in-range');
      if (iso === rf) btn.classList.add('range-start');
      if (iso === rt) btn.classList.add('range-end');
    }

    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      const btn = document.createElement('button');
      btn.className = 'dp-day other-month';
      btn.textContent = String(day);
      const d = new Date(viewYear, viewMonth - 1, day);
      const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      if (opts.minISO && iso < opts.minISO) btn.classList.add('disabled');
      applyRangeClasses(btn, iso);
      btn.addEventListener('click', () => { handleDayClick(iso); });
      grid.appendChild(btn);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const btn = document.createElement('button');
      btn.className = 'dp-day';
      btn.textContent = String(d);
      const iso = `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      if (iso === todayISO) btn.classList.add('today');
      if (selectedISO && iso === selectedISO) btn.classList.add('selected');
      if (opts.minISO && iso < opts.minISO) btn.classList.add('disabled');
      applyRangeClasses(btn, iso);
      btn.addEventListener('click', () => { handleDayClick(iso); });
      grid.appendChild(btn);
    }

    const totalCells = Math.ceil((firstDayOfWeek + daysInMonth) / 7) * 7;
    for (let d = 1; d <= totalCells - firstDayOfWeek - daysInMonth; d++) {
      const btn = document.createElement('button');
      btn.className = 'dp-day other-month';
      btn.textContent = String(d);
      const nxt = new Date(viewYear, viewMonth + 1, d);
      const iso = `${nxt.getFullYear()}-${String(nxt.getMonth()+1).padStart(2,'0')}-${String(nxt.getDate()).padStart(2,'0')}`;
      applyRangeClasses(btn, iso);
      btn.addEventListener('click', () => { handleDayClick(iso); });
      grid.appendChild(btn);
    }
    popover.appendChild(grid);

    const footer = document.createElement('div');
    footer.className = 'dp-footer';
    let timeRowHTML = '';
    if (opts.enableTime) {
      const [initHour = '', initMinute = ''] = currentTimeValue ? currentTimeValue.split(':') : [];
      const hourOpts = '<option value=""></option>' + Array.from({length: 24}, (_, i) => {
        const v = String(i).padStart(2, '0');
        return `<option value="${v}"${initHour === v ? ' selected' : ''}>${v}</option>`;
      }).join('');
      const minOpts = '<option value=""></option>' + Array.from({length: 12}, (_, i) => {
        const v = String(i * 5).padStart(2, '0');
        return `<option value="${v}"${initMinute === v ? ' selected' : ''}>${v}</option>`;
      }).join('');
      timeRowHTML = `<div class="date-picker-time-row"><span class="mi mi-xs">schedule</span><select class="dp-time-hour">${hourOpts}</select><span class="dp-time-sep">:</span><select class="dp-time-min">${minOpts}</select><span class="date-picker-time-hint">時刻（任意）</span></div>`;
    }
    footer.innerHTML = `
      ${timeRowHTML}
      <span class="dp-hint">${opts.hint || ''}</span>
      <div style="display:flex;gap:6px;align-items:center;">
        ${opts.clearable !== false ? '<button class="dp-clear">クリア</button>' : ''}
        ${opts.enableTime ? '<button class="dp-save">保存</button>' : ''}
      </div>
    `;
    popover.appendChild(footer);
    const dpTimeHour = popover.querySelector('.dp-time-hour') as HTMLSelectElement | null;
    const dpTimeMin = popover.querySelector('.dp-time-min') as HTMLSelectElement | null;
    function saveCurrentTime() {
      const h = dpTimeHour?.value || '';
      const m = dpTimeMin?.value || '';
      currentTimeValue = (h && m) ? `${h}:${m}` : '';
    }
    if (dpTimeHour) dpTimeHour.addEventListener('change', saveCurrentTime);
    if (dpTimeMin) dpTimeMin.addEventListener('change', saveCurrentTime);

    document.body.appendChild(popover);

    const rect = anchorEl.getBoundingClientRect();
    const pw = 240;
    let top = rect.bottom + 4;
    let left = rect.left;
    if (left + pw > window.innerWidth - 8) left = window.innerWidth - pw - 8;
    const ph = popover.offsetHeight;
    if (top + ph > window.innerHeight - 8) top = rect.top - ph - 4;
    popover.style.top = top + 'px';
    popover.style.left = left + 'px';
    const anchorCenterX = rect.left + rect.width / 2;
    const originX = Math.max(12, Math.min(pw - 12, anchorCenterX - left));
    popover.style.setProperty('--popover-origin-x', `${originX}px`);
    popover.style.setProperty('--popover-origin-y', top < rect.top ? 'bottom' : 'top');

    popover.querySelector('#dp-prev').addEventListener('click', e => {
      e.stopPropagation();
      saveCurrentTime();
      viewMonth--;
      if (viewMonth < 0) { viewMonth = 11; viewYear--; }
      renderPopover();
    });
    popover.querySelector('#dp-next').addEventListener('click', e => {
      e.stopPropagation();
      saveCurrentTime();
      viewMonth++;
      if (viewMonth > 11) { viewMonth = 0; viewYear++; }
      renderPopover();
    });

    const saveBtn = popover.querySelector('.dp-save');
    if (saveBtn) {
      saveBtn.addEventListener('click', e => {
        e.stopPropagation();
        const h = dpTimeHour?.value || '';
        const m = dpTimeMin?.value || '';
        const timeStr = (h && m) ? `${h}:${m}` : '';
        opts.onSave({ dateISO: selectedISO, timeStr: timeStr || '00:00', hasTime: !!(h && m) });
        popover.remove();
        cleanup();
      });
    }

    const clearBtn = popover.querySelector('.dp-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', e => {
        e.stopPropagation();
        if (opts.enableTime) {
          opts.onSave({ dateISO: null, timeStr: '00:00', hasTime: false });
        } else {
          opts.onSave(null);
        }
        popover.remove();
        cleanup();
      });
    }
  }

  renderPopover();
  setTimeout(() => {
    document.addEventListener('mousedown', outsideClick);
    document.addEventListener('keydown', escapeKey);
  }, 0);
}

/** The connected AI takes the hold off the calendar; Zenmetry only reads the calendar. */
export async function dismissHold(goalId: string, eventId: string, _eventTitle?: string): Promise<boolean> {
  try {
    await api(`/api/goals/${encodeURIComponent(goalId)}/holds/${encodeURIComponent(eventId)}/release`, { method: 'POST' });
    await loadAllGoals();
    showToast(currentLang === 'en' ? 'Asked the AI to remove the hold.' : '仮押さえを外すよう AI に頼みました', 'success');
    return true;
  } catch (err) {
    showToast(t('updateError', { msg: getErrorMessage(err) }) as string);
    return false;
  }
}

export function patchCachedGoal(goalId: string, updates: Partial<Goal>) {
  const idx = cachedAllGoals.findIndex(g => g.id === goalId);
  if (idx !== -1) Object.assign(cachedAllGoals[idx], updates);
}

export function rerenderGoalCard(goalId: string) {
  const goal = cachedAllGoals.find(g => g.id === goalId);
  if (!goal) return;
  // #2749: the card can show twice, in すべて and in the detail beside the list.
  document.querySelectorAll(`[id="goal_${goalId}"]`).forEach((el) => {
    const host = el.closest('zen-goal-card') as (HTMLElement & { goal?: Goal; requestUpdate?: () => void }) | null;
    if (!host) return;
    host.goal = goal;
    host.requestUpdate?.();
  });
}

/** `onSaved` gets the day chosen, once it is saved. */
export function editGoalDeadline(goalId: string, el: HTMLElement, currentISO: string | null, onSaved?: (day: string | null) => void) {
  const todayISO = getLocalTodayISO();
  const goal = cachedAllGoals.find(g => g.id === goalId);
  const rangeFrom = goal?.start_at?.slice(0, 10) || null;
  const currentTime = goal?.deadline_has_time && goal?.deadline
    ? new Date(goal.deadline).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', hour12: false })
    : '';
  showDatePickerPopover(el, {
    currentISO,
    minISO: todayISO,
    rangeFrom,
    rangeTo: currentISO || null,
    hint: t('subtaskDeadlineHint') as string,
    clearable: true,
    enableTime: true,
    currentTime,
    async onSave(value) {
      if (!value || typeof value === 'string') return;
      const { dateISO, timeStr, hasTime } = value;
      const val = dateISO;
      // #2320 step 3: a date alone goes as the date, and the server ends that
      // day in the profile timezone; a time is read in this browser's timezone.
      const sent = !val ? null : hasTime ? new Date(`${val}T${timeStr}:00`).toISOString() : val;
      try {
        const resp = await api(`/api/goals/${goalId}`, { method: 'PATCH', json: { deadline: sent, deadline_has_time: hasTime } });
        // #2320: the server's reading of the new deadline (overdue) replaces the old one.
        const dates = resp?.goal?.dates ?? null;
        const deadline = resp?.goal?.deadline ?? sent;

        // #2681: tasks that inherit the Goal deadline follow it on the server;
        // a deadline the principal set stays.
        for (const st of goal?.subtasks || []) {
          if (st.deadline_inherited === false || st.status === 'done' || st.status === 'invalidated') continue;
          updateCachedSubtask(st.id, subtask => ({ ...subtask, deadline }));
        }
        patchCachedGoal(goalId, { deadline, deadline_has_time: hasTime, dates });
        renderWebGoalState();
        showToast(t('deadlineUpdated') as string, 'info');
        onSaved?.(val || null);
      } catch (err) {
        showToast(goalUpdateErrorMessage(err, 'deadlineUpdateError'));
      }
    }
  });
}

export function toggleSubtaskDesc(subtaskId: string, btnEl: HTMLElement) {
  if (expandedSubtaskDescriptions.has(subtaskId)) {
    expandedSubtaskDescriptions.delete(subtaskId);
  } else {
    expandedSubtaskDescriptions.add(subtaskId);
  }
  renderWebGoalState();
}

export function editSubtaskTitle(subtaskId: string, el: HTMLElement) {
  const currentText = el.textContent.trim();
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'subtask-title-input';
  input.value = currentText;
  el.replaceWith(input);
  input.focus();
  input.select();

  let saved = false;
  async function save() {
    if (saved) return;
    saved = true;
    const val = input.value.trim();
    if (!val || val === currentText) {
      renderWebGoalState();
      return;
    }
    try {
      await api(`/api/subtasks/${subtaskId}`, { method: 'PATCH', json: { title: val } });
      updateCachedSubtask(subtaskId, subtask => ({ ...subtask, title: val }));
      renderWebGoalState();
      showToast(t('titleUpdated') as string, 'info');
    } catch (err) {
      showToast(t('updateError', { msg: getErrorMessage(err) }) as string);
      renderWebGoalState();
    }
  }

  input.addEventListener('blur', save);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
    if (e.key === 'Escape') { saved = true; renderWebGoalState(); }
  });
}

/** Saves a goal's new title and draws the goals again, whether it was saved or not. */
export async function saveGoalTitle(goalId: string, title: string) {
  try {
    await api(`/api/goals/${goalId}`, { method: 'PATCH', json: { title } });
    patchCachedGoal(goalId, { title });
    renderWebGoalState();
    showToast(t('titleUpdated') as string, 'info');
  } catch (err) {
    showToast(goalUpdateErrorMessage(err, 'updateError'));
    renderWebGoalState();
  }
}

export function editGoalTitle(goalId: string, el: HTMLElement) {
  const currentText = el.textContent.trim();
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'goal-title-input';
  input.value = currentText;
  el.replaceWith(input);
  input.focus();
  input.select();

  let saved = false;
  async function save() {
    if (saved) return;
    saved = true;
    const val = input.value.trim();
    if (!val || val === currentText) {
      renderWebGoalState();
      return;
    }
    await saveGoalTitle(goalId, val);
  }

  input.addEventListener('blur', save);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
    if (e.key === 'Escape') { saved = true; renderWebGoalState(); }
  });
}

// #2494: a Goal whose DoD copies its lens conditions is edited row by row,
// and the save sends condition operations with the lens version it read.
function editGoalDodRows(goal: Goal, el: HTMLElement) {
  const rows = goal.dod_rows ?? [];
  const editor = document.createElement('zen-goal-dod-rows-editor') as HTMLElement & { rows: GoalDodRow[]; saving: boolean };
  editor.rows = rows;
  el.replaceWith(editor);
  editor.addEventListener('dod-rows-cancel', () => renderWebGoalState());
  editor.addEventListener('dod-rows-save', async (event: Event) => {
    const { edits } = (event as CustomEvent<GoalDodRowsSaveDetail>).detail;
    if (edits.length === 0) { renderWebGoalState(); return; }
    editor.saving = true;
    await saveGoalDodRows(goal, edits);
  });
}

/** True when the goal's conditions are edited row by row (#2494). */
export function hasGoalDodRows(goal: Goal): boolean {
  return Array.isArray(goal.dod_rows) && goal.dod_rows.length > 0 && typeof goal.lens_version === 'number';
}

/**
 * Saves row edits with the lens version they were read at, then draws the
 * goals again. An edit of a judgment with a current choice is refused until
 * the principal answers 「保留にして直しますか」 (#2537); yes sends it again
 * with the hold.
 */
export async function saveGoalDodRows(goal: Goal, edits: GoalDodRowsSaveDetail['edits'], holdJudgmentChoices = false) {
  try {
    const response = await api(`/api/goals/${goal.id}`, {
      method: 'PATCH',
      json: {
        dod_edits: { expected_lens_version: goal.lens_version, edits, ...(holdJudgmentChoices ? { hold_judgment_choices: true } : {}) },
        edit_entrypoint: 'web_dod_edit',
      },
    });
    const saved = (response as { goal?: Partial<Goal> } | null)?.goal;
    patchCachedGoal(goal.id, saved
      ? { dod: saved.dod, dod_rows: saved.dod_rows, lens_version: saved.lens_version, goal_dod_items: saved.goal_dod_items, reconcile_status: saved.reconcile_status }
      : {});
    renderWebGoalState();
  } catch (err) {
    if (!holdJudgmentChoices && (err as { code?: unknown } | null)?.code === 'goal_judgment_hold_required') {
      renderWebGoalState();
      showConfirmModal(t('dodRowsHoldConfirm') as string, t('dodRowsHoldConfirmAction') as string,
        () => { void saveGoalDodRows(goal, edits, true); }, 'pause_circle');
      return;
    }
    showToast(goalUpdateErrorMessage(err, 'updateError'));
    renderWebGoalState();
  }
}

/** Saves the conditions written as text, then draws the goals again. */
export async function saveGoalDod(goalId: string, dod: string) {
  try {
    // #2478: PATCH re-authors the GoalContract through update_goal, so no
    // separate reanalysis follows the edit.
    await api(`/api/goals/${goalId}`, { method: 'PATCH', json: { dod: dod || null } });
    patchCachedGoal(goalId, { dod: dod || null, reconcile_status: null });
    renderWebGoalState();
  } catch (err) {
    showToast(goalUpdateErrorMessage(err, 'updateError'));
    renderWebGoalState();
  }
}

export function editGoalDod(goalId: string, el: HTMLElement) {
  const goal = cachedAllGoals.find(g => g.id === goalId);
  if (goal && hasGoalDodRows(goal)) {
    editGoalDodRows(goal, el);
    return;
  }
  const currentText = el.dataset.dodCurrent ?? el.textContent.trim();
  const textarea = document.createElement('textarea');
  textarea.className = 'goal-dod-input';
  textarea.value = currentText;
  el.replaceWith(textarea);
  textarea.focus();
  textarea.select();
  textarea.style.height = textarea.scrollHeight + 'px';

  let saved = false;
  async function save() {
    if (saved) return;
    saved = true;
    const val = textarea.value.trim();
    if (val === currentText) { renderWebGoalState(); return; }
    await saveGoalDod(goalId, val);
  }

  textarea.addEventListener('blur', save);
  textarea.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { saved = true; renderWebGoalState(); }
  });
  textarea.addEventListener('input', () => {
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  });
}

export function editSubtaskDeadline(subtaskId: string, el: HTMLElement, currentISO: string | null) {
  const todayISO = getLocalTodayISO();
  showDatePickerPopover(el, {
    currentISO: currentISO || null,
    minISO: todayISO,
    hint: t('subtaskDeadlineHint') as string,
    clearable: true,
    async onSave(val) {
      const deadline = val ? val + 'T00:00:00.000Z' : null;
      try {
        const resp = await api(`/api/subtasks/${subtaskId}`, { method: 'PATCH', json: { deadline } });
        if (resp?.warning) {
          showToast(resp.warning, 'warning');
        }
        const willDisappear = currentDeadlineFilter !== 'all' &&
          !itemMatchesDeadlineFilter(deadline, currentDeadlineFilter);
        updateCachedSubtask(subtaskId, subtask => ({ ...subtask, deadline, deadline_inherited: false }));
        renderWebGoalState();
        if (willDisappear) {
          const label = formatDeadlineBucketLabel(val);
          showToastWithAction(
            t('subtaskMovedToFilter', { label }) as string,
            t('showAction') as string,
            () => {
              const allBtn = document.querySelector<HTMLElement>('[data-deadline-filter="all"]');
              if (allBtn) setDeadlineFilter('all', allBtn);
            }
          );
        }
      } catch (err) {
        showToast(t('deadlineUpdateError', { msg: getErrorMessage(err) }) as string);
      }
    }
  });
}

export function deleteGoalUI(goalId: string, title: string, btnEl: HTMLButtonElement) {
  if (btnEl.disabled) return;
  showConfirmModal(t('confirmDeleteGoal', { title }) as string, t('delete') as string, async () => {
    // The goal detail beside the work list has no card to dim.
    const card = btnEl.closest<HTMLElement>('.goal-card');
    if (card) card.style.opacity = '0.5';
    btnEl.disabled = true;
    try {
      await api(`/api/goals/${goalId}`, { method: 'DELETE' });
      removeCachedGoal(goalId);
      renderWebGoalState();
    } catch (err) {
      showToast(t('deleteError', { msg: getErrorMessage(err) }) as string);
      if (card) card.style.opacity = '1';
      btnEl.disabled = false;
    }
  }, 'delete');
}

export async function approveAiResultUI(subtaskId: string) {
  const textarea = document.querySelector(`.ai-result-content[data-subtask-id="${subtaskId}"]`) as HTMLTextAreaElement | null;
  const resultContent = textarea?.value || null;
  try {
    const body = textarea ? { result_content: textarea.value } : {};
    const response = await api(`/api/subtasks/${subtaskId}/approve`, { method: 'POST', json: body });
    updateCachedSubtask(subtaskId, subtask => ({
      ...subtask,
      ...(response?.subtask || {}),
      ai_result: response?.result
        ? response.result
        : subtask.ai_result
          ? { ...subtask.ai_result, result_content: resultContent || subtask.ai_result.result_content }
          : subtask.ai_result,
    }));
    openRejectFeedbackIds.delete(subtaskId);
    renderWebGoalState();
    showToast(t('approve') as string, 'info');
  } catch (err) {
    const message = getErrorMessage(err);
    if (message.includes('no longer actionable') || message.includes('parent goal is closed')) {
      await loadAllGoals();
      showToast(currentLang === 'en' ? 'This task is no longer actionable because the parent goal is closed.' : '親ゴールが閉じているため、このタスクは承認できません。');
      return;
    }
    showToast(t('approveError', { msg: message }) as string);
  }
}

export function toggleRejectFeedback(subtaskId: string) {
  if (openRejectFeedbackIds.has(subtaskId)) {
    openRejectFeedbackIds.delete(subtaskId);
  } else {
    openRejectFeedbackIds.add(subtaskId);
  }
  renderWebGoalState();
}

export async function rejectAiResultUI(subtaskId: string) {
  try {
    const feedbackEl = document.getElementById(`reject-feedback-text-${subtaskId}`) as HTMLTextAreaElement | null;
    const feedback = feedbackEl ? feedbackEl.value.trim() : '';
    await api(`/api/subtasks/${subtaskId}/reject`, { method: 'POST', json: feedback ? { feedback } : {} });
    openRejectFeedbackIds.delete(subtaskId);
    await loadAllGoals();
    showToast(t('retry') as string, 'info');
  } catch (err) {
    showToast(t('rejectError', { msg: getErrorMessage(err) }) as string);
  }
}

export function toggleAiResult(subtaskId: string) {
  if (collapsedAiResults.has(subtaskId)) {
    collapsedAiResults.delete(subtaskId);
  } else {
    collapsedAiResults.add(subtaskId);
  }
  renderWebGoalState();
}

export function copyAiResult(btnEl: HTMLElement) {
  const panel = btnEl.closest<HTMLElement>('.ai-result-panel');
  if (!panel) return;
  const el = panel.querySelector<HTMLElement>('.ai-result-content');
  if (!el) return;
  const content = el instanceof HTMLTextAreaElement ? el.value : (el.textContent || '');
  navigator.clipboard.writeText(content).then(() => {
    const orig = btnEl.innerHTML;
    btnEl.innerHTML = '<span class="mi mi-xs">check</span>';
    setTimeout(() => btnEl.innerHTML = orig, 1500);
  });
}

export function inferAiAction(title: string) {
  const t = title.toLowerCase();
  if (/メール|mail|下書き|draft/.test(t)) return 'draft_message';
  if (/カレンダー|日程|スケジュール/.test(t)) return 'check_calendar';
  if (/会議|打ち合わせ|meeting/.test(t)) return 'schedule_meeting';
  if (/まとめ|要約|summarize/.test(t)) return 'summarize_info';
  if (/調べ|検索|調査|search/.test(t)) return 'lookup_info';
  if (/比較|compare|選択肢/.test(t)) return 'compare_options';
  if (/リマインダー|通知|reminder/.test(t)) return 'send_reminder';
  if (/追跡|進捗|track/.test(t)) return 'track_progress';
  return null;
}

export async function cycleExecutor(subtaskId: string, badgeEl: HTMLElement, ev: Event) {
  ev.stopPropagation();
  const current = badgeEl.getAttribute('data-executor') || 'human';
  const next = current === 'human' ? 'ai' : 'human';

  const labels = () => ({
    human: `<span class="mi mi-xs">person</span> ${currentLang === 'en' ? 'Me' : '自分'}`,
    ai: `<span class="mi mi-xs">smart_toy</span> AI`
  });
  let aiAction = undefined;
  if (next === 'ai') {
    const currentSubtask = cachedAllGoals
      .flatMap(goal => [...(goal.decisions || []), ...(goal.subtasks || [])])
      .find(subtask => subtask.id === subtaskId);
    const title = currentSubtask?.title || '';
    aiAction = inferAiAction(title);
  } else {
    aiAction = null;
  }
  updateCachedSubtask(subtaskId, subtask => ({ ...subtask, executor: next, ai_action: aiAction ?? subtask.ai_action }));
  renderWebGoalState();

  try {
    const patchBody: Record<string, string | null> = { executor: next };
    if (aiAction !== undefined) patchBody.ai_action = aiAction;
    const result = await api(`/api/subtasks/${subtaskId}`, {
      method: 'PATCH',
      json: patchBody
    }) as { subtask?: { executor?: 'human' | 'ai'; ai_action?: string | null } };
    if (result.subtask) {
      updateCachedSubtask(subtaskId, subtask => ({
        ...subtask,
        executor: result.subtask?.executor ?? subtask.executor,
        ai_action: result.subtask?.ai_action ?? null
      }));
      renderWebGoalState();
    }
  } catch (err) {
    updateCachedSubtask(subtaskId, subtask => ({ ...subtask, executor: current as 'human' | 'ai' }));
    renderWebGoalState();
    showToast(t('updateError', { msg: getErrorMessage(err) }) as string);
  }
}

export function openLightbox(src: string) {
  const safeSrc = sanitizeImageUrl(src);
  if (!safeSrc) return;
  const lb = document.getElementById('imgLightbox');
  (document.getElementById('lightboxImg') as HTMLImageElement).src = safeSrc;
  lb.style.display = 'flex';
}
